import React, {
  ChangeEvent,
  FC,
  useEffect,
  useState,
  useMemo,
  ReactNode,
  SyntheticEvent,
} from 'react';
import {
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  InputLabel,
  FormControl,
  Autocomplete,
  AutocompleteRenderInputParams,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import {
  AccountExtType,
  AccountSolution,
  CompanyDto,
  CountriesListDto,
  Country,
  UpdateCompanyDto,
} from '@thingslog/repositories';
import JwtValidator from '../../common/JwtValidator';
import { useCompanyUtils } from '../../hooks/useCompanyUtils';

const UpdateCompanyModal: FC<UpdateCompanyModalProps> = ({
  company,
  countriesListDto,
  timezones,
  updateCompany,
  setDisableAccountRefCallback,
}: UpdateCompanyModalProps) => {
  const { t } = useTranslation();
  const { solutionTypeOptions } = useCompanyUtils();
  const jwtValidator = useMemo(() => new JwtValidator(), []);
  const [isAccountRefInputDisabled, setIsAccountRefInputDisabled] = useState<boolean>(false);
  // Company fields
  const [companyName, setCompanyName] = useState<string>(company.name);
  const [accountRef, setAccountRef] = useState<string | null>(company.accountRef);
  const [contactRef, setContactRef] = useState<string | null>(company.contactRef);
  const [timezoneId, setTimezoneId] = useState<string>(company.zoneId);
  const [solutionType, setSolutionType] = useState<AccountSolution>(
    company.solution ? company.solution : AccountSolution.NONE
  );
  const [accountExtNumber, setAccountExtNumber] = useState<string>(company.accountExtNumber || '');
  const [accountExtType, setAccountExtType] = useState<AccountExtType | ''>(
    company.accountExtType || ''
  );
  // CompanyInfo fields
  const [country, setCountry] = useState<string>(company.companyInfo?.country || '');
  const [vatNumber, setVatNumber] = useState<string | null>(company.companyInfo?.vatNumber || null);
  const [address, setAddress] = useState<string | null>(company.companyInfo?.address || null);
  const [city, setCity] = useState<string | null>(company.companyInfo?.city || null);
  const [zipCode, setZipCode] = useState<string | null>(company.companyInfo?.zipCode || null);
  const [municipality, setMunicipality] = useState<string | null>(
    company.companyInfo?.municipality || null
  );
  const [area, setArea] = useState<string | null>(company.companyInfo?.area || null);

  const handleSubmit = (): void => {
    const solutionValue = solutionType !== AccountSolution.NONE ? solutionType : null;
    const companyForUpdate: UpdateCompanyDto = {
      id: company.id,
      name: companyName,
      parentId: company.parentId,
      solution: solutionValue,
      contactRef: contactRef,
      accountRef: accountRef,
      accountExtNumber: accountExtNumber || null,
      accountExtType: accountExtType || null,
      zoneId: timezoneId,
      companyInfo: {
        companyName: companyName,
        country: country,
        vatNumber: vatNumber,
        address: address,
        city: city,
        zipCode: zipCode,
        municipality: municipality,
        area: area,
        logo: company.companyInfo?.logo || null,
      },
    };
    updateCompany(companyForUpdate);
  };

  useEffect(() => {
    setDisableAccountRefCallback && setDisableAccountRefCallback(setIsAccountRefInputDisabled);
  }, []);

  return (
    <>
      <div>
        <div className="flex p-5 justify-center items-center space-y-5 flex-col">
          <div className="w-full font-bold">{t('company_manager_account')}</div>
          {jwtValidator.hasRole('ROLE_SUPER_ADMIN') && (
            <>
              <TextField
                fullWidth
                autoFocus
                value={companyName}
                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                  setCompanyName(event.target.value)
                }
                label={t('company_manager_account_name')}
                name="group_name"
                size="small"
                variant="outlined"
                error={!companyName}
                required
              />
              <FormControl fullWidth>
                <InputLabel>{t('company_manager_solution_type')}</InputLabel>
                <Select
                  variant="outlined"
                  label={t('company_manager_solution_type')}
                  value={solutionType}
                  size="small"
                  onChange={(event: SelectChangeEvent): void =>
                    setSolutionType(event.target.value as AccountSolution)
                  }
                >
                  {solutionTypeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                value={accountRef}
                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                  setAccountRef(event.target.value)
                }
                label={t('accounts_accountRef')}
                size="small"
                variant="outlined"
                disabled={
                  !!contactRef ||
                  isAccountRefInputDisabled ||
                  !jwtValidator.hasRole('ROLE_SUPER_ADMIN')
                }
                inputProps={{ maxLength: 12 }}
              />
              {isAccountRefInputDisabled && (
                <div className="text-xs text-gray-500">
                  {t('company_manager_accountRef_is_disabled_because_of_license_type')}
                </div>
              )}
              <TextField
                fullWidth
                value={contactRef}
                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                  setContactRef(event.target.value)
                }
                label={t('accounts_contactRef')}
                size="small"
                variant="outlined"
                disabled={!!accountRef || !jwtValidator.hasRole('ROLE_SUPER_ADMIN')}
                inputProps={{ maxLength: 8 }}
              />
            </>
          )}
          {jwtValidator.hasRoles(['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']) && (
            <>
              <TextField
                fullWidth
                value={accountExtNumber}
                onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                  setAccountExtNumber(event.target.value)
                }
                label={t('company_manager_account_ext_number')}
                name="accountExtNumber"
                size="small"
                variant="outlined"
              />
              <FormControl fullWidth>
                <InputLabel>{t('company_manager_account_ext_type')}</InputLabel>
                <Select
                  variant="outlined"
                  size="small"
                  label={t('company_manager_account_ext_type')}
                  value={accountExtType}
                  onChange={(event: SelectChangeEvent): void =>
                    setAccountExtType(event.target.value as AccountExtType | '')
                  }
                >
                  <MenuItem value="">{t('company_manager_account_ext_type_none')}</MenuItem>
                  <MenuItem value={AccountExtType.HOUSEHOLD}>
                    {t('company_manager_account_ext_type_household')}
                  </MenuItem>
                  <MenuItem value={AccountExtType.BUSINESS}>
                    {t('company_manager_account_ext_type_business')}
                  </MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          <Autocomplete
            defaultValue={timezoneId}
            className="w-full"
            options={Array.from(timezones || [])}
            value={timezoneId}
            onChange={(event: SyntheticEvent, newValue: string | null): void =>
              setTimezoneId(newValue as string)
            }
            renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
              <TextField
                required
                {...params}
                error={!timezoneId}
                label={t('company_manager_timezone')}
                size="small"
              />
            )}
          />
          <div className="w-full font-bold">{t('company_manager_account_info')}</div>
          <Autocomplete
            defaultValue={country}
            className="w-full"
            options={
              countriesListDto
                ? countriesListDto.countries.map((country: Country) => country.name)
                : []
            }
            value={country}
            onChange={(event: SyntheticEvent, value: string | null): void => {
              setCountry(value || '');
            }}
            renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
              <TextField
                error={!country}
                required
                {...params}
                label={t('company_manager_country')}
                size="small"
              />
            )}
          />
          <TextField
            fullWidth
            value={address}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              setAddress(event.target.value)
            }
            label={t('company_manager_address')}
            size="small"
            variant="outlined"
          />
          <TextField
            fullWidth
            value={city}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => setCity(event.target.value)}
            label={t('company_manager_city')}
            size="small"
            variant="outlined"
          />
          <TextField
            fullWidth
            value={municipality}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              setMunicipality(event.target.value)
            }
            label={t('company_manager_municipality')}
            size="small"
            variant="outlined"
          />
          <TextField
            fullWidth
            value={area}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => setArea(event.target.value)}
            label={t('company_manager_area')}
            size="small"
            variant="outlined"
          />
          <TextField
            fullWidth
            value={zipCode}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              setZipCode(event.target.value)
            }
            label={t('company_manager_zip_code')}
            size="small"
            variant="outlined"
          />
          <TextField
            fullWidth
            value={vatNumber}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              setVatNumber(event.target.value)
            }
            label={t('company_manager_vat_number')}
            size="small"
            variant="outlined"
          />
          <Button
            variant="contained"
            color={'primary'}
            disabled={!companyName || !country}
            className="w-full"
            onClick={(): void => {
              handleSubmit();
            }}
          >
            {t('company_manager_update_btn')}
          </Button>
        </div>
      </div>
    </>
  );
};

interface UpdateCompanyModalProps {
  updateCompany: (body: UpdateCompanyDto) => void;
  company: CompanyDto;
  timezones: Set<string>;
  countriesListDto: CountriesListDto | null;
  setDisableAccountRefCallback?: (accountRefUpdater: (disabled: boolean) => void) => void;
}

export default UpdateCompanyModal;
